import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, RefreshCw, SearchX, CheckCircle, ArrowLeft } from 'lucide-react';
import { NWSAdapter } from '../services/nwsAdapter';
import { NormalizedAlert, AlertFilters, AlertResponseMetadata, AlertSeverity } from '../types/alert';
import { AlertCard } from '../components/alerts/AlertCard';
import { AlertFilterBar } from '../components/alerts/AlertFilterBar';
import { FreshnessIndicator } from '../components/common/FreshnessIndicator';
import { DisclaimerBanner } from '../components/common/DisclaimerBanner';
import { AdSlot } from '../components/common/AdSlot';
import { getSeverityRank } from '../utils/formatters';

export const AlertSearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [alerts, setAlerts] = useState<NormalizedAlert[]>([]);
  const [metadata, setMetadata] = useState<AlertResponseMetadata | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Initialize filters from URL parameters if available
  const [filters, setFilters] = useState<AlertFilters>({
    searchQuery: searchParams.get('q') || '',
    severity: (searchParams.get('severity') as AlertSeverity) || 'ALL',
    category: 'ALL',
    state: searchParams.get('state') || 'ALL',
    sortBy: 'severity',
  });

  const loadData = async (force: boolean = false) => {
    try {
      if (force) setIsRefreshing(true);
      else setLoading(true);
      setError(null);

      // If state is selected, fetch specifically for that state, else fetch national
      const stateParam = filters.state !== 'ALL' ? filters.state : undefined;
      const res = await NWSAdapter.fetchActiveAlerts({ stateCode: stateParam, forceRefresh: force });
      setAlerts(res.alerts);
      setMetadata(res.metadata);
    } catch (err: any) {
      setError(err.message || 'Failed to retrieve official alerts from National Weather Service');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData(false);
  }, [filters.state]);

  // Keep URL parameters in sync
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.searchQuery) params.set('q', filters.searchQuery);
    if (filters.severity !== 'ALL') params.set('severity', filters.severity);
    if (filters.state !== 'ALL') params.set('state', filters.state);
    setSearchParams(params, { replace: true });
  }, [filters.searchQuery, filters.severity, filters.state]);

  // Filter and sort alerts
  const filteredAlerts = useMemo(() => {
    let result = [...alerts];

    // Search query filter (matches event name, headline, affected areas, description)
    if (filters.searchQuery.trim()) {
      const q = filters.searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.event.toLowerCase().includes(q) ||
          a.headline.toLowerCase().includes(q) ||
          a.areaDesc.toLowerCase().includes(q) ||
          a.senderName.toLowerCase().includes(q)
      );
    }

    // Severity filter
    if (filters.severity !== 'ALL') {
      result = result.filter((a) => a.severity === filters.severity);
    }

    // Sorting
    if (filters.sortBy === 'severity') {
      result.sort((a, b) => getSeverityRank(b.severity) - getSeverityRank(a.severity));
    } else if (filters.sortBy === 'newest') {
      result.sort((a, b) => new Date(b.effective).getTime() - new Date(a.effective).getTime());
    } else if (filters.sortBy === 'expiring') {
      result.sort((a, b) => {
        if (!a.expires) return 1;
        if (!b.expires) return -1;
        return new Date(a.expires).getTime() - new Date(b.expires).getTime();
      });
    }

    return result;
  }, [alerts, filters]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Header breadcrumb & title */}
      <div>
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mb-2 font-medium">
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Radar Overview</span>
        </Link>
        <h1 className="text-2xl sm:text-3xl font-black text-navy-950">
          Official Active Disruption Alerts
        </h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-1">
          Search and filter verified public weather advisories across the United States.
        </p>
      </div>

      {/* Freshness banner */}
      <FreshnessIndicator
        metadata={metadata}
        onRefresh={() => loadData(true)}
        isRefreshing={isRefreshing}
      />

      {/* Filter Bar */}
      <AlertFilterBar
        filters={filters}
        onChange={setFilters}
        totalResults={filteredAlerts.length}
      />

      {/* Error state */}
      {error && (
        <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-center space-y-3">
          <AlertTriangle className="w-8 h-8 text-red-600 mx-auto" />
          <h2 className="text-base font-bold text-red-950">Source Data Error</h2>
          <p className="text-xs text-red-800 max-w-lg mx-auto">{error}</p>
          <button
            onClick={() => loadData(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg transition-colors"
          >
            Retry Official Source
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-48 bg-slate-100 rounded-xl animate-pulse border border-slate-200" />
          ))}
        </div>
      )}

      {/* Empty State vs Checked Location with No Alerts */}
      {!loading && !error && filteredAlerts.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 sm:p-12 text-center max-w-xl mx-auto space-y-4">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto border border-emerald-100">
            <CheckCircle className="w-6 h-6" />
          </div>
          <div className="space-y-1">
            <h2 className="text-lg font-bold text-navy-950">
              No Active Alerts Matching Criteria
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              We checked the National Weather Service and found zero active weather alerts matching your search filters for this location.
            </p>
          </div>
          <p className="text-xs text-amber-800 bg-amber-50 p-2.5 rounded-lg border border-amber-200">
            Notice: The absence of an active alert on SignalNear does not guarantee conditions are entirely benign. Always heed local road signs and municipal emergency officials.
          </p>
          <button
            onClick={() =>
              setFilters({
                searchQuery: '',
                severity: 'ALL',
                category: 'ALL',
                state: 'ALL',
                sortBy: 'severity',
              })
            }
            className="px-4 py-2 bg-slate-100 text-navy-900 text-xs font-semibold rounded-lg hover:bg-slate-200"
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* Results Grid */}
      {!loading && !error && filteredAlerts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAlerts.map((alert) => (
            <AlertCard key={alert.id} alert={alert} />
          ))}
        </div>
      )}

      {/* Mid-results reserved ad slot */}
      <AdSlot slotId="search-bottom" format="horizontal" />

      {/* Safety banner */}
      <DisclaimerBanner />
    </div>
  );
};

